"use client";

import React from "react";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/motion";
import { ArrowRight, CheckCircle2, Smartphone, Tablet, Watch } from "lucide-react";

const DEVICES: { key: string; fallback: string; icon: typeof Smartphone }[] = [
  { key: "cf.compat.d1", fallback: "iPhone 11 and newer", icon: Smartphone },
  { key: "cf.compat.d2", fallback: "Galaxy S20 and newer", icon: Smartphone },
  { key: "cf.compat.d3", fallback: "Pixel 7 and newer", icon: Smartphone },
  { key: "cf.compat.d4", fallback: "iPad with eSIM", icon: Tablet },
  { key: "cf.compat.d5", fallback: "Selected travel tablets", icon: Tablet },
  { key: "cf.compat.d6", fallback: "Wearables with eSIM", icon: Watch },
];

/**
 * Coastal Freedom — Compatibility preview (DESIGN_BRIEF §4). Bright white card,
 * navy headings, blue accents (no glass/blur — coastal archetype). Reassures
 * vacation travelers their phone is ready and links to the shared device
 * checker. Claim-safe: lists common supported model families, no fabricated
 * device counts or guarantees.
 */
export default function CompatibilityPreview() {
  const { t } = useTranslations();

  return (
    <section className="container mx-auto px-4 py-10">
      <Reveal as="div" y={24} className="cf-card overflow-hidden p-6 sm:p-8">
        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <div>
            <span className="inline-flex rounded-full bg-primary/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-primary">
              {t("cf.compat.eyebrow", "Compatibility preview")}
            </span>
            <h2 className="mt-3 text-2xl font-extrabold text-foreground sm:text-3xl">
              {t("cf.compat.title", "Check your phone before you pack")}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground sm:text-base">
              {t("cf.compat.sub", "Most recent iPhone, Samsung Galaxy, Pixel and iPad models support eSIM. Run a quick device check before you leave so activation feels easy on day one.")}
            </p>
            <Link href="/compatible-phones/" className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground transition-all active:scale-[0.97] hover:bg-primary/90 hover:-translate-y-0.5">
              {t("cf.compat.cta", "Check my device")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <Stagger as="div" className="grid gap-3 sm:grid-cols-2" staggerChildren={0.08} amount={0.25}>
            {DEVICES.map(({ key, fallback, icon: Icon }) => (
              <StaggerItem key={key} y={20} className="rounded-[22px] border border-border/70 bg-white/80 p-4 shadow-[0_16px_30px_-24px_hsl(200_60%_30%_/_0.45)] transition-all hover:-translate-y-1 hover:border-primary/40">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </span>
                <div className="mt-3 flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  <span className="text-sm font-semibold text-foreground">{t(key, fallback)}</span>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </Reveal>
    </section>
  );
}
